import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { WordCloudConfig, WordCloudWord } from './types'
import { placeWordsOnSphere, projectSphereWords } from './wordCloudSphereLayout'
import { sizeWordCloudWords } from './wordCloudSizing'
import {
  applyArcballDrag,
  applyAutoRotationY,
  INITIAL_SPHERE_QUAT,
  Quat,
} from './wordCloudQuaternion'

const ROTATION_SPEED_Y = 0.1

export function WordCloudSphereBody({
  config,
  words,
}: {
  config: WordCloudConfig
  words: WordCloudWord[]
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
  const [rotation, setRotation] = useState<Quat>(INITIAL_SPHERE_QUAT)
  const [isDragging, setIsDragging] = useState(false)
  const animationFrameRef = useRef<number>()
  const lastTimestampRef = useRef<number>()
  const isDraggingRef = useRef(false)
  const dragStartRef = useRef<{ x: number; y: number; rotation: Quat }>()

  const sizedWords = useMemo(() => sizeWordCloudWords(words, config), [config, words])
  const sphereWords = useMemo(() => placeWordsOnSphere(sizedWords), [sizedWords])

  const projectedWords = useMemo(
    () => projectSphereWords(sphereWords, containerSize.width, containerSize.height, rotation),
    [containerSize.height, containerSize.width, rotation, sphereWords],
  )

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) {
      return
    }

    const updateSize = () => {
      const { width, height } = container.getBoundingClientRect()
      setContainerSize({ width, height })
    }

    updateSize()

    const observer = new ResizeObserver(updateSize)
    observer.observe(container)

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const tick = (timestamp: number) => {
      const lastTimestamp = lastTimestampRef.current ?? timestamp
      const deltaSeconds = (timestamp - lastTimestamp) / 1000
      lastTimestampRef.current = timestamp

      if (!isDraggingRef.current) {
        setRotation((current) => applyAutoRotationY(current, deltaSeconds * ROTATION_SPEED_Y))
      }

      animationFrameRef.current = requestAnimationFrame(tick)
    }

    animationFrameRef.current = requestAnimationFrame(tick)

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
      lastTimestampRef.current = undefined
    }
  }, [])

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const container = containerRef.current
    if (!container) {
      return
    }

    const rect = container.getBoundingClientRect()
    event.currentTarget.setPointerCapture(event.pointerId)
    isDraggingRef.current = true
    setIsDragging(true)
    dragStartRef.current = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
      rotation,
    }
  }

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const container = containerRef.current
    if (!isDraggingRef.current || !dragStartRef.current || !container) {
      return
    }

    const rect = container.getBoundingClientRect()

    setRotation(
      applyArcballDrag(
        dragStartRef.current.rotation,
        dragStartRef.current.x,
        dragStartRef.current.y,
        event.clientX - rect.left,
        event.clientY - rect.top,
        rect.width,
        rect.height,
      ),
    )
  }

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) {
      return
    }

    event.currentTarget.releasePointerCapture(event.pointerId)
    isDraggingRef.current = false
    setIsDragging(false)
    dragStartRef.current = undefined
  }

  if (!sizedWords.length) {
    return (
      <div className="text-muted-foreground flex h-full items-center justify-center p-4">
        No data available
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className={`relative h-full w-full overflow-hidden p-4 select-none touch-none ${
        isDragging ? 'cursor-grabbing' : 'cursor-grab'
      }`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {projectedWords.map((word) => (
        <span
          key={word.text}
          className="pointer-events-none absolute whitespace-nowrap font-semibold leading-none"
          style={{
            left: word.screenX,
            top: word.screenY,
            fontSize: word.fontSize * word.depthScale,
            color: word.color,
            opacity: word.opacity,
            transform: 'translate(-50%, -50%)',
          }}
        >
          {word.text}
        </span>
      ))}
    </div>
  )
}
